import { EventType } from "@/analytics/analytics-events";
import {
  ApplyEditWaypointsChangesProperties,
  DiscardEditWaypointsChangesProperties,
} from "@/analytics/edit-waypoints-tool-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  selectEditWaypointsAnalyticsStats,
  useApplyChangesInEditWaypointsStore,
  useEditWaypointsStore,
  useResetEditWaypointsStore,
} from "@/modes/sheet-mode/edit-waypoints/edit-waypoints-store";
import { useAppDispatch } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback } from "react";

/**
 * Returns a callback to handle application of waypoint changes
 *
 * @param onStopEditing To be called when editing is completed
 * @param via The component that is calling this function, for analytics
 * @returns A callback to handle application of waypoint changes
 */
export function useApplyEditWaypointsChanges(
  onStopEditing: () => void,
  via: ApplyEditWaypointsChangesProperties["via"],
): () => Promise<void> {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();
  const applyChangesInEditWaypointsStore =
    useApplyChangesInEditWaypointsStore();

  return useCallback(async () => {
    Analytics.track<ApplyEditWaypointsChangesProperties>(
      EventType.applyEditWaypointsChanges,
      {
        via,
        ...selectEditWaypointsAnalyticsStats(useEditWaypointsStore.getState()),
      },
    );

    dispatch(setShowSpinner(true));

    try {
      await applyChangesInEditWaypointsStore();

      onStopEditing();
    } catch (error) {
      handleErrorWithToast({
        title: "Could not apply changes",
        error,
      });
    } finally {
      dispatch(setShowSpinner(false));
    }
  }, [
    applyChangesInEditWaypointsStore,
    dispatch,
    handleErrorWithToast,
    onStopEditing,
    via,
  ]);
}

/**
 * Returns a callback to handle cancellation of waypoint changes
 *
 * @param onStopEditing To be called when editing is completed
 * @param via The component that is calling this function, for analytics
 * @returns A callback to handle cancellation of waypoint changes
 */
export function useCancelEditWaypoints(
  onStopEditing: () => void,
  via: DiscardEditWaypointsChangesProperties["via"],
): () => void {
  const resetEditWaypointsStore = useResetEditWaypointsStore();

  return useCallback(() => {
    Analytics.track<DiscardEditWaypointsChangesProperties>(
      EventType.discardEditWaypointsChanges,
      {
        via,
      },
    );

    resetEditWaypointsStore();

    onStopEditing();
  }, [onStopEditing, resetEditWaypointsStore, via]);
}
